import { Badge, Button, Group, Image, Stack, Text } from '@mantine/core'
import { ReactNode } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import { CopyableText } from '@/components/ui-shared/CopyableText/CopyableText'
import {
  TrackedPerson,
  TrackedPersonMatch,
  TrackedPersonState
} from '@/types/event'

const CROP_HEIGHT = 220

const MATCH_CONFIG = {
  [TrackedPersonMatch.Perfect]: {
    color: 'teal',
    translationId: 'events.trackedPeople.match.perfect'
  },
  [TrackedPersonMatch.Face]: {
    color: 'blue',
    translationId: 'events.trackedPeople.match.face'
  },
  [TrackedPersonMatch.Appearance]: {
    color: 'gray',
    translationId: 'events.trackedPeople.match.appearance'
  }
}

const WAITING_MESSAGE_ID = {
  [TrackedPersonState.Matching]: 'events.trackedPeople.matching',
  [TrackedPersonState.OneCamera]: 'events.trackedPeople.oneCamera'
}

type LabelledIdProps = {
  label: ReactNode
  id?: string | null
  fallback?: ReactNode
}

const LabelledId = ({ label, id, fallback }: LabelledIdProps) => {
  return (
    <Stack gap={2}>
      <Text size="xs" c="dimmed" fw={600}>
        {label}
      </Text>

      {id ? (
        <CopyableText text={id}>
          <Text size="sm" ff="monospace" style={{ wordBreak: 'break-all' }}>
            {id}
          </Text>
        </CopyableText>
      ) : (
        <Text size="sm" c="dimmed">
          {fallback}
        </Text>
      )}
    </Stack>
  )
}

type EventTrackedPeopleProps = {
  people: TrackedPerson[]
  canSplit?: boolean
  splittingTrackingId?: string
  onFindLookalikesClick: (trackingId: string) => void
  onNotSamePersonClick: (trackingId: string) => void
}

export const EventTrackedPeople = ({
  people,
  canSplit,
  splittingTrackingId,
  onFindLookalikesClick,
  onNotSamePersonClick
}: EventTrackedPeopleProps) => {
  const intl = useIntl()

  return (
    <Stack gap="lg">
      {people.map(({ tracking_id, identity_id, crop_path, state, match }) => (
        <Group key={tracking_id} wrap="nowrap" align="flex-start" gap="md">
          {crop_path && (
            <Image
              h={CROP_HEIGHT}
              w="auto"
              fit="contain"
              src={crop_path}
              alt={intl.formatMessage({ id: 'crossCamera.people.crop' })}
            />
          )}

          <Stack gap="sm">
            <LabelledId
              label={<FormattedMessage id="events.trackedPeople.uniqueId" />}
              id={identity_id}
              fallback={
                state !== TrackedPersonState.Identified && (
                  <FormattedMessage id={WAITING_MESSAGE_ID[state]} />
                )
              }
            />

            {identity_id && match && MATCH_CONFIG[match] && (
              <Badge
                color={MATCH_CONFIG[match].color}
                variant="light"
                tt="none"
              >
                <FormattedMessage id={MATCH_CONFIG[match].translationId} />
              </Badge>
            )}

            <LabelledId
              label={<FormattedMessage id="events.trackedPeople.trackerId" />}
              id={tracking_id}
            />

            <Group gap="xs">
              <Button
                variant="light"
                size="xs"
                onClick={() => onFindLookalikesClick(tracking_id)}
              >
                <FormattedMessage id="events.trackedPeople.findLookalikes" />
              </Button>

              {canSplit && identity_id && (
                <Button
                  variant="subtle"
                  color="red"
                  size="xs"
                  loading={splittingTrackingId === tracking_id}
                  onClick={() => onNotSamePersonClick(tracking_id)}
                >
                  <FormattedMessage id="events.trackedPeople.notSamePerson" />
                </Button>
              )}
            </Group>
          </Stack>
        </Group>
      ))}
    </Stack>
  )
}
